import { toast } from "react-toastify";
import qs from "qs";
import NEW_API from "../../Api/NewApi";
import { isDefined } from "../../Utils";
import { scopeParams } from "./appCustomers";

// ---------------------------------------------------------------------------
// Commercials (a store's sales reps, not user accounts) — /api/commercials,
// per-organization scoping enforced server-side. Rows keep the Strapi v4
// envelope ({ id, attributes }) like the competitor table.
// ---------------------------------------------------------------------------

// `user`: the commercial's own SOFT login, when it has one (status only).
const LIST_POPULATE = {
  organization: { fields: ["id", "name"] },
  user: { fields: ["id", "blocked"] },
};

export const fetchCommercials =
  (search, page = 1, signal) =>
  async (dispatch, getState) => {
    try {
      if (page === 1) dispatch({ type: "FETCH_COMMERCIALS_LOADING" });
      const query = qs.stringify({
        populate: LIST_POPULATE,
        sort: ["lastName:asc", "firstName:asc"],
        pagination: { page, pageSize: 25 },
        ...(search
          ? {
              filters: {
                $or: ["lastName", "firstName", "email", "phoneNumber"].map(
                  (field) => ({ [field]: { $containsi: search } })
                ),
              },
            }
          : {}),
        ...scopeParams(getState),
      });
      const { data } = await NEW_API.get(`api/commercials?${query}`, {
        signal,
      });
      dispatch({
        type:
          page === 1
            ? "FETCH_COMMERCIALS_SUCCESS"
            : "FETCH_COMMERCIALS_LOAD_MORE_SUCCESS",
        payload: data,
      });
    } catch (error) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED")
        return;
      console.log(error);
      dispatch({ type: "FETCH_COMMERCIALS_ERROR", payload: error });
    }
  };

/**
 * `password` (optional, from the form): gives the commercial its SOFT access
 * with it, or changes the access's password — once the fiche is saved.
 */
export const createUpdateCommercial =
  (formData, id, t, callback, password) => async (dispatch) => {
    dispatch({ type: "CREATE_UPDATE_COMMERCIAL_LOADING" });
    try {
      const data = { ...formData, email: formData.email || null };
      // Store picked by an admin / business affiliate (a reseller's is set
      // server-side); absent = unchanged.
      if (formData.organization !== undefined) {
        data.organization = formData.organization?.code ?? null;
      }
      const query = qs.stringify({ populate: LIST_POPULATE });
      const { data: response } = await NEW_API[isDefined(id) ? "put" : "post"](
        `api/commercials/${id ?? ""}?${query}`,
        { data }
      );
      dispatch({
        type: isDefined(id)
          ? "UPDATE_COMMERCIAL_SUCCESS"
          : "CREATE_COMMERCIAL_SUCCESS",
        payload: response?.data,
      });
      toast.success(
        isDefined(id)
          ? t("Commercial successfully updated!")
          : t("Commercial successfully created!")
      );
      if (password) await dispatch(setAccessPassword(response?.data, password, t));
      if (callback) callback(response?.data);
      return true;
    } catch (error) {
      console.log(error);
      dispatch({ type: "CREATE_UPDATE_COMMERCIAL_ERROR" });
      toast.error(t("An error has occurred!"));
      return false;
    }
  };

export const deleteCommercial = (id, t) => async (dispatch) => {
  try {
    dispatch({ type: "DELETE_COMMERCIAL_LOADING" });
    await NEW_API.delete(`api/commercials/${id}`);
    dispatch({ type: "DELETE_COMMERCIAL_SUCCESS", payload: id });
    toast.success(t("Commercial successfully deleted!"));
  } catch (error) {
    console.log(error);
    dispatch({ type: "DELETE_COMMERCIAL_ERROR" });
    toast.error(t("An error has occurred!"));
  }
};

const ACCOUNT_ERRORS = {
  EMAIL_REQUIRED: "Add the commercial's email first",
  EMAIL_TAKEN: "This email already has a SOFT account",
  ACCOUNT_EXISTS: "This commercial already has an access",
  PASSWORD_TOO_SHORT: "PASSWORD_TOO_SHORT",
};

const accountError = (error, t) =>
  toast.error(
    t(ACCOUNT_ERRORS[error?.response?.data?.error?.message] ?? "An error has occurred!")
  );

/** Reloads row `id` (its access status). */
const reloadCommercial = (id) => async (dispatch) => {
  const query = qs.stringify({ populate: LIST_POPULATE });
  const { data } = await NEW_API.get(`api/commercials/${id}?${query}`);
  dispatch({ type: "UPDATE_COMMERCIAL_SUCCESS", payload: data?.data });
};

/** The form's password: creates the access with it, or changes it. */
const setAccessPassword = (row, password, t) => async (dispatch) => {
  const hadAccess = !!row?.attributes?.user?.data;
  try {
    await NEW_API[hadAccess ? "put" : "post"](`api/commercials/${row.id}/account`, {
      data: { password },
    });
    await dispatch(reloadCommercial(row.id));
    toast.success(
      t(hadAccess ? "Password updated" : "Access created with this password")
    );
  } catch (error) {
    console.log(error);
    accountError(error, t);
  }
};

/**
 * A commercial's SOFT login: "create" (sends the invitation), "invite"
 * (sends it again), "block" / "unblock". The row is then reloaded.
 */
export const commercialAccount = (id, action, t) => async (dispatch) => {
  try {
    const path = `api/commercials/${id}/account`;
    if (action === "create") await NEW_API.post(path);
    else if (action === "invite") await NEW_API.post(`${path}/invite`);
    else await NEW_API.put(path, { data: { blocked: action === "block" } });
    await dispatch(reloadCommercial(id));
    toast.success(
      t(
        {
          create: "Access created: the commercial received an email to set its password",
          invite: "Invitation sent again",
          block: "Access disabled",
          unblock: "Access enabled",
        }[action]
      )
    );
  } catch (error) {
    console.log(error);
    accountError(error, t);
  }
};

/**
 * "Commercial" picker options (project sheet / list, order sheet): the
 * commercials of store `organizationId` (a reseller always gets its own),
 * as [{ name, code }].
 */
export const fetchCommercialOptions = async (organizationId, signal) => {
  const query = qs.stringify({
    fields: ["firstName", "lastName"],
    sort: ["lastName:asc", "firstName:asc"],
    pagination: { pageSize: 200 },
    ...(organizationId ? { organizationId } : {}),
  });
  const { data } = await NEW_API.get(`api/commercials?${query}`, { signal });
  return (data?.data ?? []).map((row) => ({
    name: [row.attributes?.firstName, row.attributes?.lastName]
      .filter(Boolean)
      .join(" "),
    code: row.id,
  }));
};
